import React, {
  useEffect, useState, useRef, Fragment,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayoutContent, FormLayout } from 'components/FormLayout';
import FormButtons from 'components/FormButtons';
import FormPanel from 'components/FormPanel';
import * as sshSecurityInfoSelectors from 'data/sshSecurityInfo/sshSecurityInfo.selectors';
import { RootState } from 'data/root.reducers';
import { ConnectedProps, connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import * as sshSecurityInfoActions from 'data/sshSecurityInfo/sshSecurityInfo.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import SshSecurityInformationForm from 'routes/SshSecurityInformation/components/SshSecurityInformationForm';
import { SshSecurityInfo } from 'data/sshSecurityInfo/sshSecurityInfo.types';
import { sshSecurityInfoTemplate } from '../../templates';
import { adaptTemplateVisibilityMode } from '../../utils/sshSecurityInformationTemplate.adapter';

type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean
  getValues: () => SshSecurityInfo
}

type RouterParams = {
  history: string
}
const mapState = (state: RootState) => ({
  deliveryFunctionId: state.global.selectedDF,
  selectedSshSecurityInfo: sshSecurityInfoSelectors
    .getSelectedSshSecurityInfos(state)[0],
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  originalSelectedSshSecurityInfo: sshSecurityInfoSelectors
    .getSelectedSshSecurityInfos(state)[0],
  sshSecurityInfo: sshSecurityInfoSelectors.getSshSecurityInfo(state),
});

const mapDispatch = {
  goToSshSecurityInfoList: sshSecurityInfoActions.goToSshSecurityInfoList,
  goToSshSecurityInfoEdit: sshSecurityInfoActions.goToSshSecurityInfoEdit,
  getSshSecurityInfo: sshSecurityInfoActions.getSshSecurityInfo,
};
const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const SshSecurityInformationView = ({
  history,
  selectedSshSecurityInfo,
  deliveryFunctionId,
  sshSecurityInfo,
  goToSshSecurityInfoList,
  goToSshSecurityInfoEdit,
  getSshSecurityInfo,
}: Props): JSX.Element => {

  const formRef = useRef<Ref>(null);
  const [formData, setFormData] = useState<Partial<SshSecurityInfo>>({});
  const [template, setTemplate] = useState(adaptTemplateVisibilityMode(sshSecurityInfoTemplate, 'view'));
  const { t } = useTranslation();

  const id = selectedSshSecurityInfo?.id;

  useEffect(() => {
    if (id) {
      getSshSecurityInfo(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, id, getSshSecurityInfo]);

  useEffect(() => {
    if (sshSecurityInfo == null || template == null) {
      return;
    }

    const clonedSshSecurityInfo = cloneDeep(sshSecurityInfo);
    const clonedTemplate = cloneDeep(template);
    clonedTemplate.formId = Date.now().toString();

    setTemplate(clonedTemplate);
    setFormData(clonedSshSecurityInfo);
  }, [sshSecurityInfo, template]);

  const handleClickEdit = () => goToSshSecurityInfoEdit(history);

  const handleCancel = () => goToSshSecurityInfoList(history);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    if (formData != null) {
      return;
    }

    const clonedFormData = cloneDeep(formData as SshSecurityInfo);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const renderForm = () => {
    if (formData && template) {
      return (
        <SshSecurityInformationForm
          ref={formRef}
          data={formData}
          template={template}
          isEditing
          onFieldUpdate={handleFieldUpdate}
        />
      );
    }
  };
  const renderFormPanel = (formTitle:string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing={false}
          onClickCancel={handleCancel}
          onClickEdit={handleClickEdit}
          onClickClose={handleCancel}
        />
      </FormPanel>
    );
  };

  if (sshSecurityInfo == null) {
    return <Fragment />;
  }

  return (
    <div data-testid="SshSecurityInformationView">
      <FormLayout>
        <FormLayoutContent>
          {renderFormPanel(t('sshSecurityInfo.view.title'))}
          <div className="read-only">
            {renderForm()}
          </div>
        </FormLayoutContent>
      </FormLayout>
    </div>
  );
};

export default withRouter(connector(SshSecurityInformationView));
